/**
 * v4.9.0 — MyTap Access Modal (per-employee).
 *
 * Operational identity panel for hospitality managers. Surfaces:
 *  - Current MyTap status (not_invited / invited / active)
 *  - Assigned cards + voucher totals (read-only summary)
 *  - Send invite / Resend invite / Reset password / Generate temp password /
 *    Force reset on next sign-in / Deactivate / Reactivate
 *  - Recent access events (audit log)
 *
 * Trilingual via `useTranslation()`. No plaintext password storage — temp
 * passwords are shown ONCE in a copy-to-clipboard panel.
 */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
    Mail, KeyRound, RefreshCw, Copy, AlertCircle, ShieldOff, ShieldCheck,
    Send, Loader2,
} from "lucide-react";
import { InlineHelper } from "../setup_guide/InlineHelper";
import { parseUtc } from '../../lib/datetime';

const STATUS_TONE = {
    not_invited: { label: "mytap.status.not_invited", tone: "slate" },
    invited:     { label: "mytap.status.invited",     tone: "amber" },
    active:      { label: "mytap.status.active",      tone: "emerald" },
};

const ToneBadge = ({ tone, children, testId }) => {
    const palette = {
        slate:   { bg: "rgba(148,163,184,0.15)", color: "#94A3B8", border: "rgba(148,163,184,0.30)" },
        amber:   { bg: "rgba(245,158,11,0.14)",  color: "#FBBF24", border: "rgba(245,158,11,0.32)" },
        emerald: { bg: "rgba(16,185,129,0.14)",  color: "#A7F3D0", border: "rgba(16,185,129,0.30)" },
        crimson: { bg: "rgba(248,113,113,0.14)", color: "#FCA5A5", border: "rgba(248,113,113,0.30)" },
    }[tone] || {};
    return (
        <span
            data-testid={testId}
            className="inline-flex items-center text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: palette.bg, color: palette.color, border: `1px solid ${palette.border}`, fontFamily: "Outfit, sans-serif" }}
        >
            {children}
        </span>
    );
};

export const MyTapAccessModal = ({ open, onClose, employee }) => {
    const { t, i18n } = useTranslation();
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tempPwd, setTempPwd] = useState(null);

    const empId = employee?.id;

    const load = useCallback(async () => {
        if (!empId) return;
        try {
            const res = await api.get(`/employees/${empId}/profile-summary`);
            setSummary(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("mytap.access.load_failed"));
        }
    }, [empId, t]);

    useEffect(() => {
        if (open) {
            setTempPwd(null);
            load();
        }
    }, [open, load]);

    const lang = (i18n.language || "en").split("-")[0];

    const doInvite = async () => {
        setBusy(true);
        try {
            const res = await api.post(`/employees/${empId}/invite`, { language: lang });
            toast.success(res.data?.sent ? t("mytap.access.invite_sent") : t("mytap.access.invite_queued"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("mytap.access.invite_failed"));
        } finally { setBusy(false); }
    };

    const doResend = async () => {
        setBusy(true);
        try {
            await api.post(`/employees/${empId}/resend-invite`, { language: lang });
            toast.success(t("mytap.access.invite_sent"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("mytap.access.invite_failed"));
        } finally { setBusy(false); }
    };

    const doResetPwd = async () => {
        if (!window.confirm(t("mytap.access.reset_confirm"))) return;
        setBusy(true);
        try {
            await api.post(`/employees/${empId}/reset-password`);
            toast.success(t("mytap.access.reset_done"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("mytap.access.reset_failed"));
        } finally { setBusy(false); }
    };

    const doTempPwd = async (emailToEmployee) => {
        setBusy(true);
        try {
            const res = await api.post(`/employees/${empId}/temp-password`, {
                email_to_employee: !!emailToEmployee,
                language: lang,
            });
            setTempPwd(res.data?.temp_password);
            toast.success(t("mytap.access.temp_generated"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("mytap.access.temp_failed"));
        } finally { setBusy(false); }
    };

    const doForceReset = async (force) => {
        setBusy(true);
        try {
            await api.post(`/employees/${empId}/force-reset`, { force });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("mytap.access.update_failed"));
        } finally { setBusy(false); }
    };

    const doDeactivate = async (deactivate) => {
        if (deactivate && !window.confirm(t("mytap.access.deactivate_confirm"))) return;
        setBusy(true);
        try {
            await api.post(`/employees/${empId}/deactivate`, { deactivate });
            toast.success(deactivate ? t("mytap.access.deactivated") : t("mytap.access.reactivated"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("mytap.access.update_failed"));
        } finally { setBusy(false); }
    };

    const copyTemp = async () => {
        if (!tempPwd) return;
        try { await navigator.clipboard.writeText(tempPwd); toast.success(t("mytap.access.copied")); }
        catch { /* noop */ }
    };

    if (!open || !employee) return null;
    const emp = summary?.employee || {};
    const status = emp.mytap_status || "not_invited";
    const statusMeta = STATUS_TONE[status] || STATUS_TONE.not_invited;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mytap-access-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <KeyRound size={18} strokeWidth={1.5} className="text-amber-400" />
                        {t("mytap.access.title")}
                    </DialogTitle>
                    <DialogDescription>{t("mytap.access.subtitle")}</DialogDescription>
                </DialogHeader>

                {/* Identity row */}
                <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b">
                    <div>
                        <p className="font-medium text-sm">{employee.name}</p>
                        <p className="text-xs text-muted-foreground">
                            {employee.email || t("mytap.access.no_email")}{emp.employee_code ? ` · ID ${emp.employee_code}` : ""}
                        </p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <ToneBadge tone={statusMeta.tone} testId={`mytap-status-${status}`}>
                            {t(statusMeta.label)}
                        </ToneBadge>
                        {emp.is_deactivated && (
                            <ToneBadge tone="crimson" testId="mytap-deactivated-badge">
                                {t("mytap.access.deactivated_badge")}
                            </ToneBadge>
                        )}
                    </div>
                </div>

                {/* Temp password panel — shown once after generation */}
                {tempPwd && (
                    <div
                        data-testid="mytap-temp-password-panel"
                        className="mb-4 p-4 rounded-lg border border-amber-400/30"
                        style={{ background: "rgba(245,158,11,0.06)" }}
                    >
                        <div className="flex items-start gap-2 mb-2">
                            <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium">{t("mytap.access.temp_shown_once_title")}</p>
                                <p className="text-xs text-muted-foreground">{t("mytap.access.temp_shown_once_subtitle")}</p>
                                <p className="text-xs text-muted-foreground mt-1">{t("setup_guide.mytap.temp_password_helper")}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 p-2 rounded bg-background border">
                            <code className="font-mono text-base flex-1 select-all" data-testid="mytap-temp-password-value">{tempPwd}</code>
                            <Button size="sm" variant="ghost" onClick={copyTemp} data-testid="mytap-temp-password-copy">
                                <Copy size={14} />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Activation block */}
                <div className="space-y-2 mb-4">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {t("mytap.access.activation_section")}
                    </p>
                    <InlineHelper testId="mytap-invite-helper">
                        {t("setup_guide.mytap.invite_helper")}
                    </InlineHelper>
                    <div className="flex flex-wrap gap-2">
                        {status === "not_invited" && (
                            <Button onClick={doInvite} disabled={busy || !employee.email} size="sm" data-testid="mytap-invite-btn">
                                {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                                {t("mytap.access.invite_button")}
                            </Button>
                        )}
                        {status !== "not_invited" && (
                            <Button onClick={doResend} disabled={busy || !employee.email} variant="outline" size="sm" data-testid="mytap-resend-btn">
                                <RefreshCw size={14} className="mr-1.5" />
                                {t("mytap.access.resend_button")}
                            </Button>
                        )}
                        <Button onClick={() => doTempPwd(false)} disabled={busy} variant="outline" size="sm" data-testid="mytap-temp-show-btn">
                            <KeyRound size={14} className="mr-1.5" />
                            {t("mytap.access.temp_show_button")}
                        </Button>
                        {employee.email && (
                            <Button onClick={() => doTempPwd(true)} disabled={busy} variant="outline" size="sm" data-testid="mytap-temp-email-btn">
                                <Mail size={14} className="mr-1.5" />
                                {t("mytap.access.temp_email_button")}
                            </Button>
                        )}
                        <Button onClick={doResetPwd} disabled={busy} variant="outline" size="sm" data-testid="mytap-reset-btn">
                            {t("mytap.access.reset_button")}
                        </Button>
                    </div>
                </div>

                {/* Toggles */}
                <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                            <p className="text-sm font-medium">{t("mytap.access.force_reset_label")}</p>
                            <p className="text-xs text-muted-foreground">{t("mytap.access.force_reset_helper")}</p>
                        </div>
                        <Switch
                            checked={!!emp.force_password_reset}
                            onCheckedChange={doForceReset}
                            disabled={busy}
                            data-testid="mytap-force-reset-switch"
                        />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                            <p className="text-sm font-medium">{t("mytap.access.deactivate_label")}</p>
                            <p className="text-xs text-muted-foreground">{t("mytap.access.deactivate_helper")}</p>
                        </div>
                        {emp.is_deactivated ? (
                            <Button onClick={() => doDeactivate(false)} disabled={busy} variant="outline" size="sm" data-testid="mytap-reactivate-btn">
                                <ShieldCheck size={14} className="mr-1.5" /> {t("mytap.access.reactivate_button")}
                            </Button>
                        ) : (
                            <Button onClick={() => doDeactivate(true)} disabled={busy} variant="outline" size="sm" data-testid="mytap-deactivate-btn">
                                <ShieldOff size={14} className="mr-1.5" /> {t("mytap.access.deactivate_button")}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Voucher + cards summary */}
                {summary && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="p-3 rounded-lg border">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t("mytap.access.cards_summary")}</p>
                            <p className="text-2xl font-light" data-testid="mytap-cards-count">{summary.cards.length}</p>
                        </div>
                        <div className="p-3 rounded-lg border">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t("mytap.access.vouchers_summary")}</p>
                            <p className="text-sm">
                                <span data-testid="mytap-voucher-issued">{summary.voucher_totals.issued}</span> {t("mytap.access.vouchers_issued")} · {" "}
                                <span data-testid="mytap-voucher-redeemed">{summary.voucher_totals.redeemed}</span> {t("mytap.access.vouchers_redeemed")}
                            </p>
                        </div>
                    </div>
                )}

                {/* Recent events */}
                {summary?.recent_access_events?.length > 0 && (
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                            {t("mytap.access.recent_events")}
                        </p>
                        <ul className="text-xs space-y-1 max-h-32 overflow-y-auto" data-testid="mytap-access-audit-list">
                            {summary.recent_access_events.slice(0, 8).map((row, i) => (
                                <li key={i} className="flex items-center justify-between gap-2 py-1 border-b border-border/40 last:border-0">
                                    <span className="text-foreground">{t(`mytap.access.action.${row.action}`, row.action)}</span>
                                    <span className="text-muted-foreground">{parseUtc(row.at).toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MyTapAccessModal;
